<template>
  <template v-if="!loading">
    <vl-grid v-vl-spacer:bottom.medium class="section">
      <vl-column width="8" width-xs="12">
        <vl-title tag-name="h3"> {{ $t('productentity.detail.title') }} </vl-title>
      </vl-column>
      <vl-column v-if="editStore.canEdit" width="4" width-xs="12">
        <vl-action-group mod-align-right>
          <vl-button
            icon="plus"
            mod-icon-before
            :mod-disabled="editStore.isCreate(ProductProcessDefinitionEditType.ProductEntity)"
            @click="editStore.openCreate(ProductProcessDefinitionEditType.ProductEntity)"
            >{{ $t('general.add') }}</vl-button
          >
        </vl-action-group>
      </vl-column>
    </vl-grid>
    <vl-grid :class="{ edit: editStore.isCreate(ProductProcessDefinitionEditType.ProductEntity) }">
      <vl-column>
        <ProductEntityCreate
          v-if="editStore.isCreate(ProductProcessDefinitionEditType.ProductEntity)"
          :specification-id="specificationId"
        />
      </vl-column>
    </vl-grid>
    <vl-grid v-vl-spacer:bottom.medium class="section">
      <vl-column width="12" width-xs="12">
        <vl-accordion-list v-if="items && items.length">
          <vl-accordion-list-item v-for="(entity, i) in items" :id="i" :key="i">
            <vl-accordion :id="i.toString()" :title="getLabel(entity)">
              <vl-grid :class="{ edit: editStore.isEdit(ProductProcessDefinitionEditType.ProductEntity, entity.id) }">
                <vl-column width="8">
                  <ProductEntityEdit
                    v-if="editStore.isEdit(ProductProcessDefinitionEditType.ProductEntity, entity.id)"
                    ref="editForm"
                    :detail="entity"
                    :specification-id="specificationId"
                  />
                  <ProductEntityRead v-else :detail="entity" />
                </vl-column>
                <vl-column v-if="editStore.canEdit" width="4">
                  <vl-action-group
                    v-if="entity && !editStore.isEdit(ProductProcessDefinitionEditType.ProductEntity, entity.id)"
                    mod-align-right
                    mod-space
                    between
                  >
                    <vl-button
                      icon="pencil"
                      mod-icon-before
                      mod-secondary
                      :mod-disabled="editStore.editing"
                      @click="editStore.openEdit(ProductProcessDefinitionEditType.ProductEntity, entity.id)"
                      >{{ $t('general.edit') }}</vl-button
                    >
                    <vl-button
                      v-if="!hasTransformation(entity.id) && !hasLink(entity.id)"
                      icon="trash"
                      mod-icon-before
                      mod-error
                      @click="showDeleteModal(entity.id)"
                      >{{ $t('general.delete') }}</vl-button
                    >
                  </vl-action-group>
                  <vl-action-group v-else mod-align-right mod-space between>
                    <vl-button icon="save" x mod-icon-before :mod-loading="reloading" @click="save">{{
                      $t('general.save')
                    }}</vl-button>
                    <vl-button icon="cross" mod-icon-before mod-secondary mod-error @click="editStore.closeEdit">
                      {{ $t('general.cancel') }}
                    </vl-button>
                  </vl-action-group>
                </vl-column>
              </vl-grid>
            </vl-accordion>
          </vl-accordion-list-item>
        </vl-accordion-list>
        <template v-else>
          <vl-annotation>{{ $t('productentity.detail.empty') }}</vl-annotation>
        </template>
      </vl-column>
    </vl-grid>
  </template>
</template>
<script lang="ts" setup>
import ProductEntityCreate from './ProductEntityCreate.vue';
import ProductEntityRead from './ProductEntityRead.vue';
import ProductEntityEdit from './ProductEntityEdit.vue';
import { ProductEntityDetailDto } from '@/api/backoffice/clients';
import { useProductEntityStore } from '@/modules/configuration/store/product-entity/product-entity.store';
import {
  ProductProcessDefinitionEditType,
  useProductProcessDefinitionEditStore
} from '@/modules/configuration/store/product-process-definition/product-process-definition-edit.store';
import { Ref, computed, onMounted, ref } from 'vue';
import { useProductTransformationStore } from '@/modules/configuration/store/product-transformation/product-transformation.store';
import { useRoute } from 'vue-router';
import { useProductEntityLinkStore } from '@/modules/configuration/store/product-entity-link/product-entity-link.store';
import { useModalStore } from '@/modules/core/store/modal.store';
import { useI18n } from 'vue-i18n';

const store = useProductEntityStore();
const transformationStore = useProductTransformationStore();
const linkStore = useProductEntityLinkStore();
const modalStore = useModalStore();

const editStore = useProductProcessDefinitionEditStore();
const route = useRoute();
const { t } = useI18n();

const editForm: Ref<typeof ProductEntityEdit | (typeof ProductEntityEdit)[]> = ref(null);

const props = defineProps({
  specificationId: {
    type: Number,
    required: true
  }
});

const loading = computed((): boolean => {
  return store.loading;
});

const reloading = computed((): boolean => {
  return store.reloading;
});

const items = computed((): ProductEntityDetailDto[] => {
  return store.bySpecification(props.specificationId);
});

onMounted(async () => {
  if (props.specificationId) {
    await store.getBySpecification(props.specificationId);
  }
});

const hasLink = (id: number): boolean => {
  return linkStore.bySpecification(props.specificationId).some((x) => x.toEntity?.id === id || x.fromEntity?.id === id);
};

const hasTransformation = (id: number): boolean => {
  return transformationStore.byDefinition(parseInt(route.params.id as string)).some((x) => x.toEntity?.id === id);
};

const save = () => {
  if (Array.isArray(editForm?.value)) {
    editForm.value[0].onSubmit();
  } else {
    editForm.value.onSubmit();
  }
};

const showDeleteModal = (entityId: number) => {
  modalStore.showDeleteModal({
    title: t('productentity.detail.delete.title'),
    props: {
      confirm: () => store.remove(props.specificationId, entityId)
    }
  });
};

const getLabel = (item: ProductEntityDetailDto): string => {
  return `${item.code} - ${item.label}`;
};
</script>

<style lang="scss" scoped>
:deep(.vl-accordion__panel) {
  padding-right: 0;
}
.vl-grid {
  flex-grow: 1;
}
.section {
  padding-left: 20px;
}
</style>
